import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getUserItemPreferences,
  getMasterEquipmentDefinitions,
  getMasterEquipment,
  getUserEquipment,
  updateUserItemPreference,
  createUserItemPreference,
  getUnreadItemTypes,
  getUnreadItemTypesByUserId,
  getTenantInfo,
} from '../services/api';
import EquipmentFilter from './EquipmentFilter';
import EquipmentList from './EquipmentList';
import SelectedEquipmentModal from './SelectedEquipmentModal';
import EquipmentSelector from './EquipmentSelector';
import EquipmentPlacer from './EquipmentPlacer';
import {
  Box,
  Flex,
  Heading,
  Button,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertDescription,
  IconButton,
  Collapse,
  VStack,
  useDisclosure,
  HStack,
  Grid,
  GridItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Switch,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useMediaQuery,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Select,
  Tooltip,
  Circle,
} from '@chakra-ui/react';
import { useBreakpointValue } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon, ViewIcon, ChatIcon, HamburgerIcon, LockIcon } from '@chakra-ui/icons';
import ChatBox from './ChatBox';
import logo from '../assets/logo.jpg';
import test from '../assets/test.png';
import GuideContent from './GuideContent';

const createInitialFilterState = (equipmentDefinitions) => {
  const initialFilterState = {
    showOnlyLiked: false,
    showRecommendedOnly: false,
    showOnlyUserEquipment: false,
    manufacturer: 'all',
    searchTerm: '',
  };

  Object.entries(equipmentDefinitions.equipment_types).forEach(([typeKey, typeDef]) => {
    if (!typeDef.fields) return;
    Object.entries(typeDef.fields).forEach(([fieldName, field]) => {
      if (field.type === 'number') {
        initialFilterState[`min_${fieldName}`] = null;
        initialFilterState[`max_${fieldName}`] = null;
      }
      if (field.type === 'select' || (field.type === 'string' && fieldName !== 'manufacturer')) {
        initialFilterState[fieldName] = '';
      }
      if (field.type === 'boolean') {
        initialFilterState[fieldName] = false;
      }
    });
  });

  return initialFilterState;
};

export default function EquipmentContent({ onFixedChange, targetUserId }) {
  const { currentUser, isFunctionEnabled } = useAuth();
  const [items, setItems] = useState([]);
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [equipmentDefinitions, setEquipmentDefinitions] = useState({});
  const [definitionsLoading, setDefinitionsLoading] = useState(true);

  // --- State ---
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState('floorplan');
  const [filterState, setFilterState] = useState({});
  const [fixedMap, setFixedMap] = useState({});
  const isWide = useBreakpointValue({ base: false, md: true });
  const sidebarDisclosure = useDisclosure({ defaultIsOpen: isWide });
  const chatDisclosure = useDisclosure({ defaultIsOpen: isWide });
  const [isTsubo, setIsTsubo] = useState(false); // 単位切替（坪）
  const [sortState, setSortState] = useState({});
  const [unreadItemTypes, setUnreadItemTypes] = useState([]);
  const [iframeError, setIframeError] = useState(false);
  const [currentIframeSrc, setCurrentIframeSrc] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  
  // 未読状態を取得する関数
  const fetchUnreadItemTypes = useCallback(async () => {
    // chat機能が無効な場合は未読通知を取得しない
    if (!isFunctionEnabled('chat')) {
      setUnreadItemTypes([]);
      return;
    }

    try {
      if (targetUserId) {
        const unreadItemTypes = await getUnreadItemTypesByUserId(targetUserId);
        setUnreadItemTypes(unreadItemTypes.item_types);
      } else {
        const unreadItemTypes = await getUnreadItemTypes();
        setUnreadItemTypes(unreadItemTypes.item_types);
      }
    } catch (error) {
      console.error('未読アイテムタイプの取得に失敗:', error);
      setUnreadItemTypes([]);
    }
  }, [currentUser, targetUserId, isFunctionEnabled]);

  useEffect(() => {
    fetchUnreadItemTypes();
    
    // chat機能が有効な場合のみ定期的に未読状態を更新
    if (isFunctionEnabled('chat')) {
      const intervalId = setInterval(() => {
        fetchUnreadItemTypes();
      }, 30000); // 30秒ごとに更新
      
      return () => clearInterval(intervalId); // コンポーネント解除時にインターバル解除
    }
  }, [fetchUnreadItemTypes]);

  useEffect(() => {
    const fetchTenantInfo = async () => {
      const tenantInfo = await getTenantInfo();
      // 外部URLの代わりにプロキシ経由でロゴを取得
      const proxyLogoUrl = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071'}/api/tenant/logo`;
      setLogoUrl(proxyLogoUrl);
    };
    fetchTenantInfo();
  }, []);

  // ユーザー登録設備の取得
  const [userEquipments, setUserEquipments] = useState([]);
  const [loadingUserEquipments, setLoadingUserEquipments] = useState(false);


  // 設備定義を取得
  const loadEquipmentDefinitions = useCallback(async () => {
    if (!currentUser) {
      setDefinitionsLoading(false);
      setError("ユーザー情報がありません。");
      return;
    }
    setDefinitionsLoading(true);
    setError(null);
    try {
      const definitions = await getMasterEquipmentDefinitions();
      if (!definitions || Object.keys(definitions).length === 0) {
        throw new Error('設備定義が空です');
      }
      setEquipmentDefinitions(definitions);
      setFilterState(createInitialFilterState(definitions));
    } catch (err) {
      console.error("設備定義の取得に失敗しました:", err);
      setError(err.message);
      setEquipmentDefinitions({});
    } finally {
      setDefinitionsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadEquipmentDefinitions();
  }, [loadEquipmentDefinitions]);


  // iframeの読み込みタイムアウトを管理
  const [loadingTimeoutId, setLoadingTimeoutId] = useState(null);

  // 利用可能なガイドページのリスト
  const AVAILABLE_GUIDES = ['Kitchen', 'Bath', 'Basin'];

  // 選択された設備が変わったら、フィルター状態を初期値に戻す
  useEffect(() => {
    if (Object.keys(equipmentDefinitions).length > 0) {
      setFilterState(createInitialFilterState(equipmentDefinitions));
    }
    
    // 前のタイムアウトをクリア
    if (loadingTimeoutId) {
      clearTimeout(loadingTimeoutId);
    }
    
    // 選択された設備のガイドページ名を生成
    const guideName = selectedEquipment.charAt(0).toUpperCase() + selectedEquipment.slice(1);
    
    // ガイドページが利用可能かチェック
    if (AVAILABLE_GUIDES.includes(guideName)) {
      // iframeの初期設定
      const targetUrl = `https://www.himawarin-lab.com/guide/${guideName}`;
      setCurrentIframeSrc(targetUrl);
      setIframeError(false);
      
      // 10秒後にエラー表示（読み込みが遅い、または無効なURLの場合）
      const timeoutId = setTimeout(() => {
        setIframeError(true);
        setCurrentIframeSrc('');
      }, 10000);
      
      setLoadingTimeoutId(timeoutId);
    } else {
      // ガイドページが存在しない場合は即座にエラー表示
      setIframeError(true);
      setCurrentIframeSrc('');
    }
  }, [selectedEquipment, equipmentDefinitions]);

  // コンポーネントアンマウント時にタイムアウトをクリア
  useEffect(() => {
    return () => {
      if (loadingTimeoutId) {
        clearTimeout(loadingTimeoutId);
      }
    };
  }, [loadingTimeoutId]);

  // 現在選択中の設備の設定を取得
  const currentEquipmentConfig = useMemo(() => {
    if (definitionsLoading || Object.keys(equipmentDefinitions).length === 0) {
      let label = '設備定義を読込中...';

      if (!definitionsLoading && Object.keys(equipmentDefinitions).length === 0) {
        label = error ? '設備定義読込エラー' : '設備定義なし';
      }
      return {
        key: selectedEquipment,
        label: label,
        enabled: false,
        icon: '❓',
        fields: []
      };
    }

    const definition = equipmentDefinitions['equipment_types'][selectedEquipment];
    if (!definition) {
      console.warn(`設備定義が見つかりません: ${selectedEquipment}`);
      return {
        key: selectedEquipment,
        label: '定義が見つかりません',
        enabled: false,
        icon: '❓',
        fields: []
      };
    }
    return {
      key: selectedEquipment,
      label: definition.name,
      enabled: definition.enabled,
      icon: definition.icon,
      fields: Object.entries(definition.fields || {}).map(([name, field]) => ({
        name,
        ...field
      }))
    };
  }, [selectedEquipment, equipmentDefinitions, definitionsLoading, error]);


// 設備アイテムとユーザー設定の読み込み (selectedEquipment や targetUserId が変わった時に実行)
const loadItemsAndPreferences = useCallback(async () => {
  if (!currentUser || definitionsLoading || !equipmentDefinitions.equipment_types || !equipmentDefinitions.equipment_types[selectedEquipment]) {
    if (!definitionsLoading && equipmentDefinitions.equipment_types && !equipmentDefinitions.equipment_types[selectedEquipment]) {
      console.warn(`選択された設備 (${selectedEquipment}) の定義が equipmentDefinitions.equipment_types に見つかりません。`);
    }
    setLoading(false);
    return;
  }
  setLoading(true);
  setError(null);
  try {
    const itemsData = await getMasterEquipment(selectedEquipment);
    const prefsData = await getUserItemPreferences(targetUserId);
    setItems(itemsData);
    setPreferences(prefsData);
  } catch (err) {
    console.error("アイテム・設定データの取得に失敗しました:", err);
    setError(err.message);
    setItems([]);
    setPreferences([]);
  } finally {
    setLoading(false);
  }
}, [currentUser, selectedEquipment, targetUserId, equipmentDefinitions, definitionsLoading]);

// selectedEquipment, targetUserId, または definitionsLoading の完了後にアイテムと設定を読み込む
useEffect(() => {
  if (!definitionsLoading) { // definitionsLoading が false になってから実行
    loadItemsAndPreferences();
  }
}, [loadItemsAndPreferences, definitionsLoading]); // definitionsLoading も依存配列に追加


  // --- ここで isFloorplanSelected を定義 ---
  const isFloorplanSelected = useMemo(() => {
    return selectedEquipment === 'floorplan';
  }, [selectedEquipment]);

  // データ結合
  const combinedData = useMemo(() => {
    // マスタ設備とユーザー設定の結合
    const masterItems = items.map(item => {
      const preference = preferences.find(p => 
        p.item_type === selectedEquipment && p.item_id === item.id
      );
      return {
        ...item,
        item_type: selectedEquipment,
        preferenceId: preference?.id,
        is_liked: preference?.is_liked ?? false,
        is_fixed: preference?.is_fixed ?? false,
        locked_at: preference?.locked_at ?? null,
        signature_url: preference?.signature_url ?? null,
        quantity: preference?.quantity ?? 1,
        memo: preference?.memo ?? null,
        material_files: preference?.material_files ?? [],
        is_user_equipment: false
      };
    });
    // ユーザー登録設備とユーザー設定の結合
    const userItems = userEquipments.map(item => {
      const preference = preferences.find(p => 
        p.item_type === selectedEquipment && p.item_id === item.id
      );
      return {
        ...item,
        id: `${item.id}`,
        item_type: selectedEquipment,
        preferenceId: preference?.id,
        is_liked: preference?.is_liked ?? false,
        is_fixed: preference?.is_fixed ?? false,
        locked_at: preference?.locked_at ?? null,
        signature_url: preference?.signature_url ?? null,
        quantity: preference?.quantity ?? 1,
        memo: preference?.memo ?? null,
        material_files: preference?.material_files ?? [],
        is_user_equipment: true
      };
    });

    return [...masterItems, ...userItems];
  }, [items, userEquipments, preferences, selectedEquipment]);
  const fetchUserEquipments = async () => {
    if (!currentUser || !selectedEquipment) return;
    setLoadingUserEquipments(true);
    try {
      const data = await getUserEquipment(selectedEquipment, targetUserId);
      setUserEquipments(data);
    } catch (err) {
      console.error("ユーザー登録設備の取得に失敗しました:", err);
      setError(err.message);
    } finally {
      setLoadingUserEquipments(false);
    }
  };

  useEffect(() => {
    fetchUserEquipments();
  }, [currentUser, selectedEquipment]);

  // フィックスされたアイテム（複数対応）
  const fixedItems = useMemo(() => {
    return combinedData.filter(item => item.item_type === selectedEquipment && (item.is_fixed || item.locked_at));
  }, [combinedData, selectedEquipment]);

  // 確定アイテムの合計金額（数量×価格）
  const fixedTotalPrice = useMemo(() => {
    return fixedItems.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);
  }, [fixedItems]);

  // 仮確定状態を親に通知
  useEffect(() => {
    if (typeof onFixedChange === 'function') {
      onFixedChange(fixedItems.length > 0);
    }
  }, [fixedItems, onFixedChange]);

  // 仮確定情報をfixedMapに反映
  useEffect(() => {
    const map = {};
    preferences.forEach(p => {
      if (p.is_fixed) map[p.item_type] = true;
    });
    setFixedMap(map);
  }, [preferences]);

  // フィルター/ソート適用後の表示データ
  const filteredData = useMemo(() => {
    if (!currentEquipmentConfig) return [];
    let result = [...combinedData];

    // 共通フィルター
    if (filterState.showRecommendedOnly) {
      result = result.filter(item => item.recommended === true);
    }
    if (filterState.showOnlyLiked) {
      result = result.filter(item => item.is_liked);
    }
    if (filterState.showOnlyUserEquipment) {
      result = result.filter(item => item.is_user_equipment);
    }
    if (filterState.manufacturer !== 'all') {
      result = result.filter(item => item.manufacturer === filterState.manufacturer);
    }
    if (filterState.searchTerm && filterState.searchTerm !== '') {
      const lowerSearchTerm = filterState.searchTerm.toLowerCase();
      result = result.filter(item =>
        item.name.toLowerCase().includes(lowerSearchTerm) ||
        (item.features && item.features.toLowerCase().includes(lowerSearchTerm))
      );
    }
    // 設備特有のフィルター
    currentEquipmentConfig.fields?.forEach(field => {
      if (field.type === 'number') {
        const minKey = `min_${field.name}`;
        const maxKey = `max_${field.name}`;
        let minValue = parseFloat(filterState[minKey]);
        let maxValue = parseFloat(filterState[maxKey]);
        if( isTsubo && field.unit === 'm2' ) {
          minValue = minValue * 3.305785;
          maxValue = maxValue * 3.305785;
        }
        if (!isNaN(minValue) || !isNaN(maxValue)) {
          result = result.filter(item => {
            const value = item[field.name] ?? -Infinity;
            return (!isNaN(minValue) ? value >= minValue : true) && 
                   (!isNaN(maxValue) ? value <= maxValue : true);
          });
        }
      }
      if (field.type === 'select' || (field.type === 'string' && field.name !== 'manufacturer')) {
        if (filterState[field.name]) {
          result = result.filter(item => item[field.name] === filterState[field.name]);
        }
      }
      if (field.type === 'boolean') {
        if (filterState[field.name]) {
          result = result.filter(item => item[field.name] === true);
        }
      }
    });

    // ソート機能
    if (sortState.key && sortState.order) {
      result = [...result].sort((a, b) => {
        const field = sortState.key;
        if (a[field] == null && b[field] == null) return 0;
        if (a[field] == null) return 1;
        if (b[field] == null) return -1;
        if (a[field] === b[field]) return 0;
        if (sortState.order === 'asc') {
          return a[field] - b[field];
        } else if (sortState.order === 'desc') {
          return b[field] - a[field];
        }
        return 0;
      });
    }

    return result;
  }, [combinedData, filterState, currentEquipmentConfig, sortState, isTsubo]);

  // 選択されたアイテム
  const selectedItem = useMemo(() => {
    if (selectedItemId === null) return null;
    return combinedData.find(item => item.id === selectedItemId) ||
           items.find(item => item.id === selectedItemId);
  }, [selectedItemId, combinedData, items]);

  // ハンドラー
  const handleItemSelect = (itemId) => { setSelectedItemId(itemId); };
  const closeModal = () => { setSelectedItemId(null); };

  // お気に入り/フィックス状態の更新処理
  const handlePreferenceChange = useCallback(async (itemId, itemType, newValueObj) => {
    setError('');
    const currentItem = combinedData.find(item => item.id === itemId);
    if (!currentItem) { console.error("Target item not found:", itemId); return; }

    // 現在のpreferenceを取得
    const currentPreference = preferences.find(
      p => p.item_type === itemType && p.item_id === itemId
    );
    const currentPreferenceId = currentPreference?.id;
    const originalPreferences = [...preferences];

    // --- Optimistic Update ---
    setPreferences(prevPreferences => {
      let preferenceExists = false;
      const newPreferences = prevPreferences.map(pref => {
        let updatedPref = { ...pref };
        if (pref.item_type === itemType && pref.item_id === itemId) {
          updatedPref = { ...updatedPref, ...newValueObj };
          preferenceExists = true;
        }
        return updatedPref;
      });
      if (!preferenceExists) {
        const newPref = {
          item_type: itemType,
          item_id: itemId,
          user_id: targetUserId || currentUser.uid,
          is_liked: newValueObj.is_liked ?? false,
          is_fixed: newValueObj.is_fixed ?? false,
          quantity: newValueObj.quantity ?? currentItem.quantity ?? 1,
          memo: newValueObj.memo ?? null,
          material_files: newValueObj.material_files ?? [],
          ...newValueObj
        };
        newPreferences.push(newPref);
      }
      return newPreferences;
    });

    try {
      let updatedPref;
      if (currentPreferenceId) {
        updatedPref = await updateUserItemPreference(
          currentPreferenceId,
          { ...newValueObj, quantity: newValueObj.quantity ?? currentItem.quantity ?? 1 },
          currentUser
        );
      } else {
        const requestData = {
          item_type: itemType,
          item_id: itemId,
          is_liked: newValueObj.is_liked ?? currentItem.is_liked,
          is_fixed: newValueObj.is_fixed ?? currentItem.is_fixed,
          quantity: newValueObj.quantity ?? currentItem.quantity ?? 1,
          memo: newValueObj.memo ?? null,
          material_files: newValueObj.material_files ?? [],
          user_id: targetUserId || currentUser.uid,
          ...newValueObj
        };
        updatedPref = await createUserItemPreference(requestData, currentUser, targetUserId);
      }
      setPreferences(prev => [
        ...prev.filter(p => !(p.item_type === itemType && p.item_id === itemId)),
        updatedPref
      ]);
    } catch (err) {
      console.error("[handlePreferenceChange] Error during preference update, reverting state:", err);
      setPreferences(originalPreferences);
      let errorMessage = '設定の更新に失敗しました';
      if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    }
  }, [combinedData, preferences, currentUser, loadItemsAndPreferences, targetUserId]);

  const uniqueManufacturers = useMemo(() => {
    const manufacturers = new Set(items.map(item => item.manufacturer));
    return [...Array.from(manufacturers).sort()];
  }, [items]);

  // 画面幅が変わったら開閉を自動で切り替える
  useEffect(() => {
    if (isWide && !sidebarDisclosure.isOpen) {
      sidebarDisclosure.onOpen();
    } else if (!isWide && sidebarDisclosure.isOpen) {
      sidebarDisclosure.onClose();
    }
    if (isWide && !chatDisclosure.isOpen) {
      chatDisclosure.onOpen();
    } else if (!isWide && chatDisclosure.isOpen) {
      chatDisclosure.onClose();
    }
    // eslint-disable-next-line
  }, [isWide]);

  const menuButtons = [
    // チャット機能が有効な場合のみ表示
    isFunctionEnabled('chat') && (
      <Button
        key="chat"
        as={RouterLink}
        to={targetUserId ? `/admin/user/${targetUserId}/chat` : '/chat'}
        size="sm"
        bg="gray.50"
        _hover={{ color: 'yellow.700' }}
        _active={{ color: 'yellow.700' }}
      >
        ご相談
        {isFunctionEnabled('chat') && unreadItemTypes.includes("anything") && (
          <Circle
            size="8px"
            bg="yellow.700"
            position="absolute"
            left="3px"
          />
        )}
      </Button>
    ),
    !targetUserId && (
      <Button
        key="visit"
        size="sm"
        as={RouterLink}
        to={'/visit-reservation'}
        bg="gray.50"
        _hover={{ color: 'yellow.700' }}
        _active={{ color: 'yellow.700' }}
      >
        ご来店
      </Button>
    ),
    <Button
      key="confirmed"
      as={RouterLink}
      to={targetUserId ? `/admin/user/${targetUserId}/confirmed-summary` : '/confirmed-summary'}
      size="sm"
      bg="gray.50"
      _hover={{ color: 'yellow.700' }}
      _active={{ color: 'yellow.700' }}
    >
      確定アイテム
    </Button>,
    <Button
      key="like"
      as={RouterLink}
      to={targetUserId ? `/admin/user/${targetUserId}/like-images` : '/like-images'}
      size="sm"
      bg="gray.50"
      _hover={{ color: 'yellow.700' }}
      _active={{ color: 'yellow.700' }}
    >
      お気に入り画像
      {unreadItemTypes.includes("like_images") && (
      <Circle
        size="8px"
        bg="yellow.700"
        position="absolute"
        left="3px"
      />
    )}
    </Button>,
    !targetUserId && (
      <Button
        key="user"
        size="sm"
        bg="gray.50"
        as={RouterLink}
        to={'/user-info'}
        _hover={{ color: 'yellow.700' }}
        _active={{ color: 'yellow.700' }}
      >
        ユーザー情報
      </Button>
    ),
  ].filter(Boolean);

  if (loading || definitionsLoading || loadingUserEquipments) {
    return (
      <Flex align="center" justify="center" minH="60vh">
        <Text fontSize="xl" color="gray.500">データを読み込み中...</Text>
      </Flex>
    );
  }

  // --- Chakra UI用の色 ---
  const headerBg = useColorModeValue('gray.50', 'gray.800');
  const headerBorder = useColorModeValue('gray.200', 'gray.700');
  const mainBg = useColorModeValue('white', 'gray.900');

  return (
    <Flex minH="100vh" w="100vw" bg={mainBg}>
      {/* サイドバー */}
      <Box
        w={sidebarDisclosure.isOpen ? '220px' : '0px'}
        minW="0px"
        bg="gray.50"
        borderRightWidth="1px" p={2}> 
        <IconButton
          icon={sidebarDisclosure.isOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          onClick={sidebarDisclosure.onToggle}
          aria-label="ナビを開閉"
          mb={2}
          size="sm"
          colorScheme="gray"
          borderWidth={1}
          borderColor="black"
//          isRound
        />
        <EquipmentSelector
          onSelect={setSelectedEquipment}
          selected={selectedEquipment}
          fixedMap={fixedMap}
          equipmentList={equipmentDefinitions}
          isOpen={sidebarDisclosure.isOpen}
          targetUserId={targetUserId}
        />
      </Box>
      {/* メインコンテンツ */}
      <Box flex={1} px={{ base: 1, md: 4 }} minW={0}>
        <Box as="header" bg={headerBg} borderBottomWidth="1px" borderColor={headerBorder} px={4} py={3} mb={2} w="100%">
          <Flex align="center" justify="space-between">
            <Box m='30px' bg="black" p={2} borderRadius="md">
              <img 
                src={logoUrl} 
                alt="logo" 
                width="300px" 
                height="90px"
                crossOrigin="anonymous"
                referrerPolicy="no-referrer"
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </Box>
            {isWide ? (
              <HStack spacing={1}>
                {menuButtons}
              </HStack>
            ) : (
              // ドロップダウンメニュー
              <Menu>
                <MenuButton as={IconButton} icon={<HamburgerIcon />} variant="outline" />
                <MenuList>
                  {menuButtons.map((btn, idx) => (
                    <MenuItem key={idx} as="div">
                      {btn}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
          </Flex>
          {/* 単位切替スイッチ（floorplanのみ） */}
          {selectedEquipment === 'floorplan' && (
            <Flex align="center" mt={2} mb={-2} gap={4}>
              <Text fontSize="sm">面積単位：m2</Text>
              <Switch isChecked={isTsubo} onChange={e => setIsTsubo(e.target.checked)} colorScheme="teal" />
              <Text fontSize="sm">坪</Text>
            </Flex>
          )}
        </Box>

        <Flex gap={4} position="relative">
          {/* 左側のメインコンテンツ */}
          <Box 
            flex={1} 
            minW="0" 
            transition="width 0.3s ease-in-out"
            w="100%"
          >
            {error && (
              <Alert status="error" mb={4} borderRadius="md">
                <AlertIcon />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Tabs variant="enclosed" colorScheme="black" mb={4}>
              <TabList>
                <Tab>選び方ガイド</Tab>
                <Tab>検索</Tab>
                <Tab>選択中アイテム</Tab>
              </TabList>

              <TabPanels>
                {/* 選び方ガイド */}
                <TabPanel>
                  <Box height="700px">
                    <Box position="relative" height="100%" width="100%">
                      {!iframeError && currentIframeSrc ? (
                        <iframe 
                          src={currentIframeSrc}
                          width="100%" 
                          height="100%"
                          onLoad={(e) => {
                            // 読み込み成功時：タイムアウトをクリアしてエラー状態をリセット
                            if (loadingTimeoutId) {
                              clearTimeout(loadingTimeoutId);
                              setLoadingTimeoutId(null);
                            }
                            
                            try {
                              // iframeの内容をチェック
                              const iframeDoc = e.target.contentDocument || e.target.contentWindow.document;
                              const bodyElement = iframeDoc.body;
                              
                              if (bodyElement) {
                                // bodyのテキストコンテンツを取得（HTMLタグを除いた純粋なテキスト）
                                const textContent = bodyElement.textContent || bodyElement.innerText || '';
                                // 空白文字を除去して実際のコンテンツがあるかチェック
                                const hasContent = textContent.trim().length > 0;
                                
                                if (!hasContent) {
                                  // テキストコンテンツが空の場合はエラーとして扱う
                                  setIframeError(true);
                                  setCurrentIframeSrc('');
                                } else {
                                  setIframeError(false);
                                }
                              } else {
                                // bodyが存在しない場合もエラー
                                setIframeError(true);
                                setCurrentIframeSrc('');
                              }
                            } catch (error) {
                              // クロスオリジンエラーなど、アクセスできない場合は正常として扱う
                              console.warn('iframe content check failed (cross-origin?):', error);
                              setIframeError(false);
                            }
                          }}
                          onError={() => {
                            // 読み込みエラー時：即座にエラー表示
                            if (loadingTimeoutId) {
                              clearTimeout(loadingTimeoutId);
                              setLoadingTimeoutId(null);
                            }
                            setIframeError(true);
                            setCurrentIframeSrc('');
                          }}
                          style={{ border: "none" }}
                        />
                      ) : (
                        <Flex
                          width="100%"
                          height="100%"
                          alignItems="center"
                          justifyContent="center"
                          flexDirection="column"
                          bg="gray.50"
                          borderWidth="1px"
                          borderColor="gray.200"
                          borderRadius="md"
                        >
                          <Heading as="h3" size="lg" mb={4} textAlign="center" color="gray.600">
                            準備中です
                          </Heading>
                          <Text fontSize="md" textAlign="center" color="gray.500">
                            書籍やWeb等でご確認ください
                          </Text>
                        </Flex>
                      )}
                    </Box>
                  </Box>
                </TabPanel>
                {/* 間取り図面 */}

                <TabPanel>
                  <Flex direction="column" gap={4} w="100%">
                    <Box w="100%">
                      <EquipmentFilter
                        config={currentEquipmentConfig}
                        filterState={filterState}
                        onFilterChange={setFilterState}
                        manufacturers={uniqueManufacturers}
                        selectedManufacturer={filterState.manufacturer}
                        onManufacturerChange={(value) => setFilterState(prev => ({ ...prev, manufacturer: value }))}
                        items={filteredData}
                        isTsubo={isTsubo}
                        sortState={sortState}
                        onSortChange={setSortState}
                      />
                    </Box>
                    <Box w="100%">
                      <EquipmentList
                        items={filteredData}
                        config={currentEquipmentConfig}
                        onPreferenceChange={handlePreferenceChange}
                        onItemSelect={handleItemSelect}
                        onReloadMaterialFiles={loadItemsAndPreferences}
                        onReloadPreferences={loadItemsAndPreferences}
                        user_item_preferences={preferences}
                        onReloadUserEquipments={fetchUserEquipments}
                        targetUserId={targetUserId || currentUser.uid}
                        isTsubo={isTsubo}
                      />
                    </Box>
                  </Flex>
                </TabPanel>
                <TabPanel>
                <VStack align="stretch" spacing={6}>
                    {fixedItems.length === 0 && (
                      <Box w="100%" p={4} bg="gray.50" borderRadius="lg" borderWidth="1px" borderColor="gray.200">
                        <Text fontSize="lg" fontWeight="bold" color="gray.700">
                          選択中のアイテムはありません
                        </Text>
                      </Box>
                    )}
                    {/* 仮確定中のアイテム（複数対応） */}
                    {fixedItems.length > 0 && (
                      <Box w="100%" p={4} bg="gray.50" borderRadius="lg" borderWidth="1px" borderColor="gray.200">
                        <Flex align="center" mb={4}>
                          <Text fontSize="lg" fontWeight="bold" color="gray.700">
                            合計: ¥{fixedTotalPrice.toLocaleString()}
                          </Text>
                        </Flex>
                        <EquipmentList
                          items={fixedItems}
                          config={currentEquipmentConfig}
                          onPreferenceChange={handlePreferenceChange}
                          onItemSelect={handleItemSelect}
                          isFixedList
                          onReloadPreferences={loadItemsAndPreferences}
                          onReloadMaterialFiles={loadItemsAndPreferences}
                          user_item_preferences={preferences}
                          onReloadUserEquipments={fetchUserEquipments}
                          targetUserId={targetUserId || currentUser.uid}
                          isTsubo={isTsubo}
                        />
                      </Box>
                    )}
                  </VStack>
                </TabPanel>

              </TabPanels>
            </Tabs>
          </Box>
          {/* チャット機能が有効な場合のみ表示 */}
          {isFunctionEnabled('chat') && (
            <>
              <IconButton
                icon={<ChatIcon boxSize="30px" />}
                onClick={chatDisclosure.onToggle}
                aria-label={chatDisclosure.isOpen ? "チャットを折りたたむ" : "チャットを展開する"}
                size="sm"
                variant="ghost"
                position="absolute"
                right={"30px"}
                top="0%"
                transform="translateY(-50%)"
                zIndex={20}
                bg="white"
                _hover={{ bg: "gray.100" }}
                boxShadow="sm"
              />
              {/* 右側のチャット画面 */}
              <Box 
                w={chatDisclosure.isOpen ? {base: "100%", sm: "400px"} : "0"}
                position="absolute" // オーバーレイ表示のため absolute に変更
                top="0" // 親要素の上端に合わせる
                right="0" // 親要素の右端に合わせる
                h="calc(100vh - 100px)"
                transition="width 0.3s ease-in-out"
                overflowY="auto" // 内容が多い場合にスクロール可能にする
                zIndex={10} // 他の要素より手前に表示
                bg="white"
                borderWidth="1px"
              >
                <ChatBox
                  itemType={selectedEquipment}
                  targetUserId={targetUserId || currentUser.uid}
                  isOpen={chatDisclosure.isOpen}
                />
              </Box>
            </>
          )}
        </Flex>

        {selectedItem && (
          <SelectedEquipmentModal
            item={selectedItem}
            config={currentEquipmentConfig}
            onClose={closeModal}
            onPreferenceChange={handlePreferenceChange}
            onUserEquipmentChange={fetchUserEquipments}
            isTsubo={isTsubo}
          />
        )}
      </Box>
    </Flex>
  );
} 